import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { sanityClient } from '../helpers/sanityClient';
import TopNavigation from '../components/TopNavigation';
import Footer from '../components/Footer';
import Hero from '../components/hero';
import imageUrlBuilder from '@sanity/image-url';
import { Helmet } from 'react-helmet-async';

interface Post {
    title: string;
    slug: { current: string };
    publishedAt: string;
    author?: string;
    mainImage?: any;
    body?: any[];
    metaDescription?: string;
    tags?: string[];
}

const builder = imageUrlBuilder(sanityClient);
const urlFor = (source: any) => builder.image(source).width(600).auto('format').url();

// helper to extract a rough text excerpt from Portable Text blocks
function getExcerpt(blocks: any[] = [], maxChars = 120) {
    if (!blocks || blocks.length === 0) return '';
    let text = '';
    for (const block of blocks) {
        if (block._type !== 'block' || !Array.isArray(block.children)) continue;
        for (const child of block.children) {
            if (child._type === 'span') {
                text += child.text;
                if (text.length >= maxChars) break;
            }
        }
        if (text.length >= maxChars) break;
    }
    if (text.length > maxChars) {
        text = text.slice(0, maxChars).trim();
        const lastSpace = text.lastIndexOf(' ');
        if (lastSpace > 0) text = text.slice(0, lastSpace);
        text += '...';
    }
    return text;
}

export default function Blog() {
    const [posts, setPosts] = useState<Post[]>([]);

    useEffect(() => {
        (sanityClient as any)
            .fetch(
                `*[_type == "post"] | order(publishedAt desc){
                    title,
                    slug,
                    publishedAt,
                    author,
                    mainImage,
                    body,
                    metaDescription,
                    tags
                }`
            )
            .then(setPosts)
            .catch(console.error);
    }, []);

    // Default meta for the blog list page
    const defaultDescription = "Read the latest recipes, tips and insights from Spelt Grazing. Our blog covers wholesome food, seasonal ingredients, and easy-to-follow guides.";
    const defaultTags = ["Spelt Grazing", "Blog", "Recipes", "Food", "Healthy Eating"];

    return (
        <>
            <Helmet>
                <title>Blog | Spelt Grazing</title>
                <meta name="description" content={defaultDescription} />
                <meta name="keywords" content={defaultTags.join(', ')} />
            </Helmet>

            <div className="flex flex-col min-h-screen">
                <TopNavigation />
                <Hero pageName={"Blog"} />
                <header className="bg-[#F9F4F2] text-gray-900 p-4">
                    <div className="max-w-7xl mx-auto flex justify-between items-center">
                        <h1 className="text-4xl font-extrabold font-typerighter">
                            Blog
                        </h1>
                    </div>
                </header>
                <main className="flex-grow bg-white py-16">
                    <div className="container mx-auto px-6 max-w-6xl">
                        <div className="grid gap-8 md:grid-cols-2">
                            {posts.map((post) => (
                                <Link
                                    key={post.slug.current}
                                    to={`/blog/${post.slug.current}`}
                                    className="group block overflow-hidden rounded-lg shadow-md bg-gray-50 transition transform hover:shadow-xl hover:-translate-y-1"
                                >
                                    <div className="relative">
                                        {post.mainImage ? (
                                            <img
                                                src={urlFor(post.mainImage)}
                                                alt={post.title}
                                                className="w-full h-48 object-cover"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className="w-full h-48 bg-gradient-to-r from-[#F3ECE6] to-[#E6D8CF]" />
                                        )}
                                        <div className="absolute top-3 left-3 px-3 py-1 bg-white bg-opacity-80 rounded text-sm font-semibold text-gray-800">
                                            {new Date(post.publishedAt).toLocaleDateString('en-GB', {
                                                day: '2-digit',
                                                month: 'short',
                                                year: 'numeric',
                                            })}
                                        </div>
                                    </div>
                                    <div className="p-6 flex flex-col h-full">
                                        <h2 className="text-xl font-semibold mb-2 font-typerighter group-hover:text-[#6B5E55] transition">
                                            {post.title}
                                        </h2>
                                        {post.author && (
                                            <p className="text-sm text-gray-600 mb-1">
                                                by {post.author}
                                            </p>
                                        )}
                                        <p className="text-gray-700 flex-grow mb-4 font-speak-pro">
                                            {getExcerpt(post.body)}
                                        </p>
                                        <div className="mt-auto flex items-center justify-between">
                                            <span className="text-sm font-semibold text-[#6B5E55]">
                                                Read more →
                                            </span>
                                            <div className="h-1 flex-1 ml-4 bg-gradient-to-r from-[#F9F8F2] to-[#E6D8CF] rounded-full" />
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </main>
                <Footer />
            </div>
        </>
    );
}
